import { useState, useEffect } from 'react';
import { Grid, TextField, Modal, Box, Button } from '@mui/material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { makeStyles } from '@mui/styles';
import PropTypes from 'prop-types';
import { signIn } from '../../../../service/CognitoAuthService';
import { ChangeFirstPassword } from '../ChangeFirstPassword/change-first-password';
import FayvenLogo from '../../../../assets/img/fayven_logo.png';
import { AlertComponent, FormButton } from '../../../../components';
import Forgot from '../Forgot/forgot';
import './style.css';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  height: 500,
  bgcolor: 'white',
  border: '0px solid #000',
  boxShadow: 24,
  p: 4,
  '@media (max-width: 600px)': {
    width: 300,
    overflowY: 'auto',
    maxHeight: 550,
  },
  '@media (max-height: 500px)': {
    width: 300,
    overflowY: 'scroll',
    maxHeight: 380,
  },
};

export default function Login({ onSuccess, onLoading, open, handleClose }) {
  const [handleErrors, setHandleErrors] = useState({
    isError: false,
    title: '',
    description: '',
  });
  const [isFirstLogin, setIsFirstLogin] = useState(null);
  const [tempData, setTempData] = useState(null);
  const [goForgot, setGoForgot] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const useStyles = makeStyles({
    input: {
      background: 'white',
    },
  });

  const handleForgot = () => setTempData(false);
  const handleOpenForgot = () => {
    setGoForgot(true);
    handleClose();
  };
  const validationSchema = yup.object({
    email: yup
      .string('Enter your email')
      .email('Enter a valid email')
      .required('Email is required'),
    password: yup
      .string('Enter your password')
      .min(10, 'Password should be of minimum 10 characters length')
      .required('Password is required'),
  });
  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      // Guard against concurrent sign-in attempts. During a slow (cold-start)
      // response users may click Login repeatedly; a second Auth.signIn() while
      // the first is pending can make Cognito/Amplify throw a spurious
      // "Email or Password Invalid" even when the credentials are correct.
      //
      // Note: we intentionally do NOT clear `submitting` on the success path.
      // Auth.signIn() resolves quickly, but the login flow isn't done until
      // onSuccess -> getUserByCognitoId finishes and navigates away (a slow
      // cold-start backend call). Keeping the button locked until navigation
      // prevents re-clicks during that window. `submitting` is only reset on
      // failure so the user can retry; on success the modal unmounts.
      if (submitting) return;
      setSubmitting(true);
      onLoading(true);
      try {
        const responseAuthentication = await signIn(values.email, values.password);
        onSuccess(responseAuthentication);
      } catch (err) {
        setHandleErrors(() => ({
          ...handleErrors,
          isError: true,
          title: 'Error Authentication',
          description: 'Email or Password Invalid',
        }));
        onLoading(false);
        setSubmitting(false);
      }
    },
  });

  useEffect(() => {
    if (isFirstLogin === undefined || isFirstLogin === null) {
      setIsFirstLogin(false);
    }
  }, [isFirstLogin]);

  // Safety net: if a previous attempt left the button locked (e.g. the
  // post-signIn user lookup failed without navigating away), reset the
  // submitting state whenever the login modal is (re)opened so the user can
  // always retry.
  useEffect(() => {
    if (open) {
      setSubmitting(false);
    }
  }, [open]);

  const classes = useStyles();
  return (
    <div>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description">
        <Box sx={style} className="form-login">
          <Grid container justifyContent="center">
            <img src={FayvenLogo} alt="logo" className="img-logo" />
          </Grid>
          {isFirstLogin ? (
            <ChangeFirstPassword tempData={tempData} />
          ) : (
            <form
              style={{ width: '100%', marginTop: '5px', marginBottom: '5px' }}
              noValidate
              onSubmit={formik.handleSubmit}>
              <TextField
                inputProps={{ className: classes.input }}
                style={{ marginTop: '5px' }}
                fullWidth
                id="email"
                name="email"
                placeholder="Email"
                type="email"
                value={formik.values.email}
                onChange={formik.handleChange}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
              />

              <TextField
                inputProps={{ className: classes.input }}
                style={{ marginTop: '5px' }}
                fullWidth
                id="password"
                name="password"
                type="password"
                placeholder="Password"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.password && Boolean(formik.errors.password)}
                helperText={formik.touched.password && formik.errors.password}
              />

              <Grid item xs={6} style={{ marginTop: '10px', marginBottom: '10px' }}>
                <Button onClick={handleOpenForgot} className="button-blank">
                  Forgot Password
                </Button>
              </Grid>
              <Grid item xs={6} />
              <FormButton type="submit" disabled={submitting}>
                {submitting ? 'Signing in...' : 'Login'}
              </FormButton>
            </form>
          )}
          {handleErrors.isError ? (
            <AlertComponent
              title={handleErrors.title}
              content={handleErrors.description}
              severity="error"
            />
          ) : null}
        </Box>
      </Modal>
      <Forgot onSuccess={handleForgot} open={goForgot} setOpen={setGoForgot} />
    </div>
  );
}

Login.propTypes = {
  onSuccess: PropTypes.func.isRequired,
  onLoading: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
};
